import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import { Icon } from "@/components/icons";

type Variant = "general" | "screening" | "monitoring";

type Option = {
  icon: string;
  title: string;
  description: string;
  href: string;
};

const OPTIONS: Record<Variant, Option[]> = {
  general: [
    {
      icon: "search",
      title: "Run a screening",
      description: "Search a single name to check against sanctions, PEP, and adverse media.",
      href: "/screening-portal",
    },
    {
      icon: "upload_file",
      title: "Batch screening",
      description: "Upload a XLSX file and screen many names. Best for periodic reviews.",
      href: "/batch",
    },
    {
      icon: "radar",
      title: "Add a name to monitor",
      description:
        "Re-screen continuously and get an alert the moment a monitored name's risk changes.",
      href: "/monitoring-portal",
    },
  ],
  screening: [
    {
      icon: "search",
      title: '"Run a screening',
      description: "Search a single name to check against sanctions, PEP, and adverse media.",
      href: "/screening-portal",
    },
    {
      icon: "upload_file",
      title: "Batch screening",
      description: "Upload a XLSX file and screen many names. Best for periodic reviews.",
      href: "/batch",
    },
  ],
  monitoring: [
    {
      icon: "radar",
      title: "Add a name to monitor",
      description:
        "Re-screen continuously and get an alert the moment a monitored name's risk changes.",
      href: "/monitoring-portal",
    },
  ],
};

const HEADINGS: Record<Variant, { title: string; description: string }> = {
  general: {
    title: "Welcome to sanctions.io",
    description: "Pick where you'd like to start.",
  },
  screening: {
    title: "Welcome to screening",
    description: "Two ways to check a name. Pick whichever fits the task in front of you.",
  },
  monitoring: {
    title: "Welcome to monitoring",
    description:
      "Add the first name you'd like to monitor. We'll alert you whenever its status changes.",
  },
};

type Props = {
  open: boolean;
  variant: Variant;
  onClose: () => void;
  /**
   * Called when the user clicks "Don't show again". Should persist the opt-out
   * to the backend so the popup never reappears after the next login.
   * Falls back to `onClose` if not provided.
   */
  onSkipForever?: () => void;
  /**
   * Left offset (px) for the backdrop so it dims only the main content region,
   * not the sidebar (Figma). The app shell passes the live sidebar width; 0 on
   * mobile (drawer), where the backdrop should cover the full viewport.
   */
  insetLeft?: number;
};

export function WelcomePopup({ open, variant, onClose, onSkipForever, insetLeft = 0 }: Props) {
  const [mounted, setMounted] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Hold the latest onClose so the focus-trap effect doesn't re-run (and fire
  // its focus-return cleanup) on every parent render — onClose is an inline
  // arrow with a fresh identity each render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    // Run only once the portal dialog is in the DOM (mounted) and open. The
    // early return also means the focus-return cleanup is registered only for a
    // genuine open→close, never for an incidental re-render.
    if (!open || !mounted) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;
    // Move focus into the dialog on open; don't steal it if it's already inside.
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      // Focus trap: keep Tab within the dialog (modal a11y baseline).
      if (e.key === "Tab" && dialog) {
        const focusables = dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      // Return focus to whatever opened the dialog.
      previouslyFocused?.focus?.();
    };
  }, [open, mounted]);

  if (!mounted || !open) return null;

  const heading = HEADINGS[variant];
  const options = OPTIONS[variant];

  return createPortal(
    <div
      // Figma dims only the main region (sidebar stays visible/interactive), so
      // the backdrop is offset from the left by the live sidebar width. The popup
      // then centers within that region rather than the whole viewport.
      className="fixed inset-y-0 right-0 z-50 flex items-center justify-center bg-grey-900-24 p-4"
      style={{ left: insetLeft }}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={heading.title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        // Cap to the viewport (minus the overlay's p-4) and scroll inside so the
        // title + "Skip for now" never get clipped on short/landscape phones.
        // outline-none: the container only takes focus programmatically for
        // screen readers; interactive children keep their visible focus rings.
        className="popup max-h-[calc(100vh-2rem)] max-w-full overflow-y-auto shadow-soft outline-none"
        style={{ width: 560 }}
      >
        <div className="flex flex-col gap-6">
          <div className="flex items-start gap-2">
            <div className="flex flex-1 flex-col gap-1">
              {/* Brand mark above the title (Figma). Logo has no mark-only variant,
                  so we reference the shared mark asset directly — the same pattern
                  sidebar.tsx and WatchlistPicker.tsx use. Decorative (alt="")
                  since the title already names the product. */}
              <img
                src="/brand/logo-collapsed.svg"
                alt=""
                width={17}
                height={20}
                className="mb-1 h-8 w-auto self-start"
              />
              <h2 className="text-h4 text-black">{heading.title}</h2>
              <p className="text-small text-grey-900">{heading.description}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex size-6 items-center justify-center text-grey-900 hover:text-black"
            >
              <Icon name="close" size={20} />
            </button>
          </div>

          <div className="flex flex-col gap-1">
            {options.map((opt) => (
              <OptionCard
                key={opt.href}
                href={opt.href}
                icon={opt.icon}
                title={opt.title}
                description={opt.description}
                onNavigate={onClose}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={onSkipForever ?? onClose}
            className="self-center text-small font-medium text-grey-600 hover:text-grey-900"
          >
            Don&apos;t show again
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function OptionCard({
  href,
  icon,
  title,
  description,
  onNavigate,
}: {
  href: string;
  icon: string;
  title: string;
  description: string;
  onNavigate: () => void;
}): ReactNode {
  return (
    <Link
      to={href}
      onClick={onNavigate}
      className="group flex items-start gap-3 rounded-sm border border-grey-200 bg-white p-4 text-left shadow-flat transition-colors hover:border-green-400 hover:bg-green-100"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-sm bg-grey-50 text-grey-900 group-hover:bg-green-600 group-hover:text-white">
        <Icon name={icon} size={20} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-body font-semibold text-black group-hover:text-green-700">
          {title}
        </span>
        <span className="text-small text-grey-900 group-hover:text-green-700">{description}</span>
      </span>
      <span className="flex size-6 shrink-0 items-center justify-center text-grey-500 group-hover:text-green-700">
        <Icon name="arrow_forward" size={20} />
      </span>
    </Link>
  );
}
